import { untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { EnumHelper } from 'mbs-ui-kit';
import { AzureAccessTierType } from 'mbs-ui-shared@models/StorageType.enum';
import { combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

export abstract class ScheduleStepBase<T extends { valid?: boolean }> extends StepBase<T> {
  public isSimpleDisabled = false;
  public invalidFFIStorageReason: {
    other?: boolean;
    storageClass?: boolean;
    azureAccessTierType?: boolean;
    syntheticFull?: boolean;
  } = {};

  protected invalidFFIStorage: string;
  protected storage: Record<string, unknown> = {};

  public get simpleDisabledMessage(): string {
    if (this.invalidFFIStorageReason.other) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIMessage.storage', { storage: this.invalidFFIStorage });
    }

    if (this.invalidFFIStorageReason.storageClass) {
      const storageClasses = StepsHelpers.getStorageClasses(true);

      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIMessage.storageClass', {
        class: storageClasses.find((c) => c.value === this.storage.storageClass)?.label
      });
    }

    if (this.invalidFFIStorageReason.azureAccessTierType) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIMessage.azureClass', {
        class: EnumHelper.EnumToSelectIndexesArray(AzureAccessTierType).find((a) => a.value === this.storage.azureAccessTierType)?.label
      });
    }

    if (this.invalidFFIStorageReason.syntheticFull) {
      return this.i18nextService.t('wizards:schedule.defaultMode.invalidFFIMessage.synthetic');
    }

    return '';
  }

  public get hasFFIProblem(): boolean {
    return Object.keys(this.invalidFFIStorageReason).some((key) => this.invalidFFIStorageReason[key]);
  }

  protected constructor(
    public mainService: RemoteManagementWizardsService,
    protected stepService: WizardStepsService,
    protected i18nextService: I18NextService
  ) {
    super();
  }

  initForm(): void {
    this.isSimpleDisabled = !this.isFFIEnabled;
  }

  protected initEvents(): void {
    const storage = this.stepService.pipeProperty('storage').pipe(startWith(this.stepService.getSnapshot('storage')));
    const retention = this.stepService.pipeProperty('retention').pipe(startWith({ isGFSMode: false }));

    combineLatest([storage, retention])
      .pipe(untilDestroyed(this))
      .subscribe(([storage, retention]) => {
        this.storage = storage;
        this.invalidFFIStorage = storage.invalidFFIStorage;
        this.invalidFFIStorageReason = { syntheticFull: this.invalidFFIStorageReason.syntheticFull };

        if (storage.invalidFFIStorage) {
          this.isSimpleDisabled = true;
          this.invalidFFIStorageReason = { other: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };

          this.deactivateSimple();

          if (StepsHelpers.isStorageBadForFFI(storage.storageClass, 'Amazon')) {
            this.invalidFFIStorageReason = { storageClass: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };
          }

          if (StepsHelpers.isStorageBadForFFI(storage.azureAccessTierType, 'Azure')) {
            this.invalidFFIStorageReason = { azureAccessTierType: true, syntheticFull: this.invalidFFIStorageReason.syntheticFull };
          }
        } else {
          this.isSimpleDisabled = !this.isFFIEnabled || this.hasFFIProblem;
        }

        this.revalidateAfterFFIReasonChange();
      });

    this.stepService
      .pipeProperty('options', 'syntheticFull')
      .pipe(untilDestroyed(this))
      .subscribe((syntheticFull) => {
        this.invalidFFIStorageReason.syntheticFull = !syntheticFull;

        this.isSimpleDisabled = !this.isFFIEnabled || this.hasFFIProblem;
        this.revalidateAfterFFIReasonChange();
      });
  }

  // Storage lands here inside the same synchronous chunk that assigns the wizard a freshly loaded FormModel,
  // before change detection delivers it; emitting now would push the step's stale value back over the loaded one
  // through the ngModel binding, and the plan would never reach the step.
  protected revalidateAfterFFIReasonChange(): void {
    setTimeout(() => this.stepForm?.updateValueAndValidity());
  }

  protected abstract deactivateSimple(): void;
}
